import { useRef, useState, useCallback, TouchEvent } from 'react'

interface SwipeConfig {
  onSwipeLeft?: () => void
  onSwipeRight?: () => void
  threshold?: number
  maxVerticalMovement?: number
}

interface SwipeState {
  isSwiping: boolean
  swipeDistance: number
}

export function useSwipe({
  onSwipeLeft,
  onSwipeRight,
  threshold = 100,
  maxVerticalMovement = 50,
}: SwipeConfig) {
  const [swipeState, setSwipeState] = useState<SwipeState>({
    isSwiping: false,
    swipeDistance: 0,
  })

  const touchStart = useRef<{ x: number; y: number } | null>(null)
  const touchCurrent = useRef<{ x: number; y: number } | null>(null)

  const handleTouchStart = useCallback((e: TouchEvent) => {
    const touch = e.touches[0]
    touchStart.current = { x: touch.clientX, y: touch.clientY }
    touchCurrent.current = { x: touch.clientX, y: touch.clientY }
  }, [])

  const handleTouchMove = useCallback((e: TouchEvent) => {
    if (!touchStart.current) return

    const touch = e.touches[0]
    touchCurrent.current = { x: touch.clientX, y: touch.clientY }

    const deltaX = touch.clientX - touchStart.current.x
    const deltaY = Math.abs(touch.clientY - touchStart.current.y)

    // Solo considerar swipe horizontal si el movimiento vertical es mínimo
    if (deltaY < maxVerticalMovement) {
      setSwipeState({
        isSwiping: true,
        swipeDistance: deltaX,
      })
    }
  }, [maxVerticalMovement])

  const handleTouchEnd = useCallback(() => {
    if (!touchStart.current || !touchCurrent.current) return

    const deltaX = touchCurrent.current.x - touchStart.current.x
    const deltaY = Math.abs(touchCurrent.current.y - touchStart.current.y)

    // Solo ejecutar swipe si el movimiento vertical es mínimo
    if (deltaY < maxVerticalMovement) {
      if (deltaX > threshold && onSwipeRight) {
        onSwipeRight()
      } else if (deltaX < -threshold && onSwipeLeft) {
        onSwipeLeft()
      }
    }

    // Reset
    touchStart.current = null
    touchCurrent.current = null
    setSwipeState({
      isSwiping: false,
      swipeDistance: 0,
    })
  }, [threshold, maxVerticalMovement, onSwipeLeft, onSwipeRight])

  return {
    swipeState,
    handlers: {
      onTouchStart: handleTouchStart,
      onTouchMove: handleTouchMove,
      onTouchEnd: handleTouchEnd,
    },
  }
}
